import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { ModuleData } from '../../Types/containerV2';
import { isAnimatedSvgModuleContent } from '../../Types/containerV2/modules/animatedSvg';
import { sanitizeInlineSvg } from '../../Utils/sanitizeInlineSvg';
import './AnimatedSvgModule.scss';

type AnimatedSvgModuleProps = {
	module: ModuleData;
};

const AnimatedSvgModule = ({ module }: AnimatedSvgModuleProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const content = isAnimatedSvgModuleContent(module.content) ? module.content : null;
	const prefersReducedMotion = usePrefersReducedMotion();
	const disableAnimation = prefersReducedMotion || content?.animationBehavior === 'disabled';
	const shouldWaitForVisibility = content?.animationBehavior === 'playWhenVisible' && !disableAnimation;
	const isVisible = useIsVisible(containerRef, shouldWaitForVisibility);
	const shouldRenderSvg = !shouldWaitForVisibility || isVisible;

	const sanitizedSvg = useMemo(
		() =>
			content && shouldRenderSvg
				? sanitizeInlineSvg(content.svgMarkup, {
						idPrefix: `animated-svg-${module.guid}`,
						ariaLabel: content.ariaLabel,
						disableAnimation,
					})
				: null,
		[content, disableAnimation, module.guid, shouldRenderSvg]
	);

	if (!content) {
		return null;
	}

	const classes = [
		'biz-module',
		'biz-animated-svg-module',
		disableAnimation ? 'biz-animated-svg-module--animation-disabled' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div ref={containerRef} className={classes}>
			<figure className='biz-animated-svg-module__figure'>
				{sanitizedSvg ? (
					<div className='biz-animated-svg-module__svg' dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />
				) : (
					<div className='biz-animated-svg-module__placeholder'>
						{!shouldRenderSvg ? 'Loading...' : content.svgMarkup?.trim() ? 'Invalid SVG markup' : 'No SVG markup'}
					</div>
				)}
				{content.caption?.trim() && (
					<figcaption className='biz-animated-svg-module__caption'>{content.caption.trim()}</figcaption>
				)}
			</figure>
		</div>
	);
};

function usePrefersReducedMotion(): boolean {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) {
			return;
		}

		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
		handleChange();
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	return prefersReducedMotion;
}

function useIsVisible(ref: RefObject<Element>, enabled: boolean): boolean {
	const [isVisible, setIsVisible] = useState(!enabled);

	useEffect(() => {
		if (!enabled) {
			setIsVisible(true);
			return;
		}

		const element = ref.current;
		if (!element || typeof IntersectionObserver === 'undefined') {
			setIsVisible(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 }
		);

		observer.observe(element);
		return () => observer.disconnect();
	}, [enabled, ref]);

	return isVisible;
}

export default AnimatedSvgModule;
